import { useState, useEffect } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts'
import { Loader } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'

const COLORS = [
  '#8b5cf6', // purple
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#06b6d4', // cyan
]

export function DynamicGraph({ selectedParameters = [], dateRange = {}, timezone = 'UTC', timePeriod = 'Y' }) {
  const [loading, setLoading] = useState(false)
  const [graphData, setGraphData] = useState([])
  const [seriesMetadata, setSeriesMetadata] = useState({})
  const [error, setError] = useState(null)

  useEffect(() => {
    console.log('[DynamicGraph] Effect triggered', {
      selectedParametersCount: selectedParameters.length,
      dateRangeFrom: dateRange.from,
      dateRangeTo: dateRange.to,
      timezone,
      timePeriod
    })

    if (!selectedParameters || selectedParameters.length === 0) {
      console.log('[DynamicGraph] No parameters selected, clearing data')
      setGraphData([])
      setSeriesMetadata({})
      return
    }

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        const allData = {}
        const metadata = {}

        // Log request details for debugging
        console.log(`[DynamicGraph] Fetching data for ${selectedParameters.length} parameters`, {
          dateRange,
          timezone,
          selectedParameters: selectedParameters.map(p => ({ id: p.id, name: p.name }))
        })

        // Fetch data for each selected parameter
        for (const param of selectedParameters) {
          try {
            // Validate that we have required fields
            if (!param.id) {
              console.error(`[DynamicGraph] Parameter missing id:`, param)
              continue
            }

            // Convert ID to number if it's a string
            const paramId = Number(param.id)
            console.log(`[DynamicGraph] Processing parameter:`, {
              originalId: param.id,
              convertedId: paramId,
              name: param.name,
              idType: typeof param.id
            })
            
            console.log(`[DynamicGraph] Fetching data for parameter ${paramId} (${param.name})`)
            
            const response = await projectsApi.fetchGraphData(paramId, {
              fromDate: dateRange.from,
              toDate: dateRange.to,
              timezone: timezone,
            })

            console.log(`[DynamicGraph] Full response for parameter ${param.id}:`, response)
            console.log(`[DynamicGraph] Response type:`, typeof response)
            console.log(`[DynamicGraph] Response is object:`, typeof response === 'object')
            
            // Check if response is an HTML error page
            if (typeof response === 'string') {
              console.error(`[DynamicGraph] Response is a STRING (likely HTML error page):`, response.substring(0, 200))
              setError('API returned HTML instead of JSON - check authentication')
              continue
            }

            // Handle error response
            if (!response) {
              console.error(`[DynamicGraph] Empty response for parameter ${param.id}`)
              continue
            }
            
            if (response.error === true) {
              console.error(`[DynamicGraph] Error in response for parameter ${param.id}:`, response?.message || response?.error || 'Unknown error')
              continue
            }

            // Validate data exists
            if (!response.data) {
              console.warn(`[DynamicGraph] No data field in response for parameter ${param.id}`)
              console.warn(`[DynamicGraph] Response keys:`, Object.keys(response))
              console.warn(`[DynamicGraph] Response structure:`, JSON.stringify(response, null, 2).substring(0, 500))
              continue
            }

            // Validate data is an array
            if (!Array.isArray(response.data)) {
              console.error(`[DynamicGraph] Response data is not an array for parameter ${param.id}. Received:`, typeof response.data, response.data)
              console.error('Full response:', response)
              continue
            }

            console.log(`[DynamicGraph] Got ${response.data.length} data points for parameter ${param.id}`)

            // Store metadata for this parameter
            metadata[param.id] = {
              name: param.name,
              unit: response.unit || param.unit || '',
              parameterName: response.parameterName,
              latestValue: response.latestValue,
              latestTimestamp: response.latestTimestamp,
            }

            // Process data points
            response.data.forEach((point) => {
              const x = point.x
              // Validate x is a valid number
              if (x === null || x === undefined || isNaN(x)) {
                console.warn(`[DynamicGraph] Skipping data point with invalid x value: ${x} for parameter ${param.id}`)
                return
              }
              // Ensure x is stored as a number
              const xAsNumber = typeof x === 'string' ? parseInt(x, 10) : x
              if (isNaN(xAsNumber)) {
                console.warn(`[DynamicGraph] Skipping data point - x could not be converted to number: ${x} for parameter ${param.id}`)
                return
              }
              if (!allData[xAsNumber]) {
                allData[xAsNumber] = { x: xAsNumber }
              }
              // Use parameter ID as key for multiple series
              allData[xAsNumber][`param_${param.id}`] = point.y
            })
          } catch (err) {
            console.error(`[DynamicGraph] Failed to fetch graph data for parameter ${param.id}:`, err.message, err)
            setError(`Failed to fetch data for ${param.name}: ${err.message}`)
          }
        }

        // Convert object to sorted array
        let processedData
        
        if (timePeriod === 'Y') {
          // Year view: Group data by month and create full 12-month timeline
          const monthGrouped = {}
          
          // Group existing data by month
          Object.entries(allData).forEach(([timestamp, data]) => {
            const date = new Date(parseInt(timestamp))
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            
            if (!monthGrouped[monthKey]) {
              monthGrouped[monthKey] = { x: monthKey }
            }
            
            // Merge parameter data
            Object.keys(data).forEach(key => {
              if (key !== 'x') {
                monthGrouped[monthKey][key] = data[key]
              }
            })
          })
          
          // Determine year range from data
          const monthKeys = Object.keys(monthGrouped).sort()
          let startYear = new Date().getFullYear()
          let startMonth = 1
          let endYear = new Date().getFullYear()
          let endMonth = 12
          
          if (monthKeys.length > 0) {
            const firstMonth = monthKeys[0].split('-')
            const lastMonth = monthKeys[monthKeys.length - 1].split('-')
            startYear = parseInt(firstMonth[0])
            startMonth = parseInt(firstMonth[1])
            endYear = parseInt(lastMonth[0])
            endMonth = parseInt(lastMonth[1])
          }
          
          // Create full date range from start to end, even across years
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          const fullRangeData = []
          
          let currentYear = startYear
          let currentMonth = startMonth
          
          while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`
            const displayLabel = `${monthNames[currentMonth - 1]} ${String(currentYear).slice(-2)}`
            
            if (monthGrouped[monthKey]) {
              fullRangeData.push({
                x: monthKey,
                displayLabel,
                ...monthGrouped[monthKey]
              })
            } else {
              // Add empty month (will show as gap in line)
              fullRangeData.push({
                x: monthKey,
                displayLabel
              })
            }
            
            // Move to next month
            currentMonth++
            if (currentMonth > 12) {
              currentMonth = 1
              currentYear++
            }
          }
          
          processedData = fullRangeData
          
          console.log(`[DynamicGraph] Year view - Months to display: ${processedData.length}`)
          console.log(`[DynamicGraph] Month keys: ${processedData.map(d => d.x).join(', ')}`)
        } else {
          // Other views: Use timestamp ordering
          processedData = Object.values(allData).sort((a, b) => a.x - b.x)
        }
        
        // Process data based on time period
        if (timePeriod === 'Y') {
          // Year view: Group by month and create full 12-month timeline
          const monthGrouped = {}
          
          // Group existing data by month
          Object.entries(allData).forEach(([timestamp, data]) => {
            const date = new Date(parseInt(timestamp))
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            
            if (!monthGrouped[monthKey]) {
              monthGrouped[monthKey] = { x: monthKey }
            }
            
            // Merge parameter data
            Object.keys(data).forEach(key => {
              if (key !== 'x') {
                monthGrouped[monthKey][key] = data[key]
              }
            })
          })
          
          // Determine year range from dateRange prop to show all 12 months
          let startYear = new Date().getFullYear()
          let startMonth = 1
          let endYear = new Date().getFullYear()
          let endMonth = 12
          
          // Use dateRange to calculate the full month span
          if (dateRange.from && dateRange.to) {
            const fromDate = new Date(dateRange.from)
            const toDate = new Date(dateRange.to)
            startYear = fromDate.getFullYear()
            startMonth = fromDate.getMonth() + 1
            endYear = toDate.getFullYear()
            endMonth = toDate.getMonth() + 1
          }
          
          // Create full date range spanning all months from start to end
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          const fullRangeData = []
          
          let currentYear = startYear
          let currentMonth = startMonth
          
          while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`
            const displayLabel = `${monthNames[currentMonth - 1]} ${String(currentYear).slice(-2)}`
            
            if (monthGrouped[monthKey]) {
              fullRangeData.push({
                x: monthKey,
                displayLabel,
                ...monthGrouped[monthKey]
              })
            } else {
              // Add empty month (will show as gap in line)
              fullRangeData.push({
                x: monthKey,
                displayLabel
              })
            }
            
            // Move to next month
            currentMonth++
            if (currentMonth > 12) {
              currentMonth = 1
              currentYear++
            }
          }
          
          processedData = fullRangeData
          
          console.log(`[DynamicGraph] Year view - Months to display: ${processedData.length}`)
          console.log(`[DynamicGraph] Month keys: ${processedData.map(d => d.x).join(', ')}`)
        } else {
          // Other views: Use timestamp ordering but filter out entries with all null values
          const timestampEntries = Object.entries(allData)
          const entriesWithData = timestampEntries.filter(([timestamp, data]) => {
            // Check if this timestamp has at least one non-null parameter value
            return Object.entries(data).some(([key, value]) => {
              return key !== 'x' && value !== null && value !== undefined
            })
          })
          
          if (entriesWithData.length === 0) {
            console.warn(`[DynamicGraph] No actual data points for ${timePeriod} view (only null values)`)
            // Return a single empty data point to trigger "No data" message
            processedData = []
          } else {
            // For D/W/M views, ensure x is a number
            processedData = entriesWithData
              .map(([, data]) => ({
                ...data,
                x: typeof data.x === 'string' ? parseInt(data.x, 10) : data.x
              }))
              .filter(item => !isNaN(item.x)) // Filter out any invalid x values
              .sort((a, b) => a.x - b.x)
          }
        }
        
        console.log(`[DynamicGraph] Processed ${processedData.length} data points for ${timePeriod} view`)
        if (processedData.length > 0) {
          console.log(`[DynamicGraph] First: ${processedData[0].x}, Last: ${processedData[processedData.length - 1].x}`)
          // Log detailed data for debugging
          console.log('[DynamicGraph] Sample data points:')
          processedData.slice(0, 3).forEach((d, idx) => {
            console.log(`  Point ${idx}:`, {
              x: d.x,
              xType: typeof d.x,
              xIsNumber: !isNaN(d.x),
              keys: Object.keys(d).slice(0, 5)
            })
          })
        } else {
          console.log(`[DynamicGraph] No data points available for this time period`)
        }
        setGraphData(processedData)
        setSeriesMetadata(metadata)
      } catch (err) {
        console.error('[DynamicGraph] Error in fetchData:', err.message, err)
        setError(err.message || 'Failed to load graph data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [selectedParameters, dateRange, timezone, timePeriod])

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded border border-gray-200">
        <div className="flex flex-col items-center gap-2">
          <Loader className="w-6 h-6 text-blue-500 animate-spin" />
          <p className="text-sm text-gray-600">Loading graph data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-red-50 rounded border border-red-200">
        <div className="text-center max-w-md">
          <p className="text-red-600 text-sm font-semibold mb-2">Error loading graph data</p>
          <p className="text-red-500 text-xs">{error}</p>
          <p className="text-red-400 text-xs mt-2">Check browser console for more details</p>
        </div>
      </div>
    )
  }

  // Format timestamp for tooltip
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    try {
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch (e) {
      return timestamp
    }
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null

    return (
      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
        <p className="text-xs font-semibold text-gray-800 mb-1">
          {formatTimestamp(label)}
        </p>
        {payload.map((entry, index) => {
          const paramId = entry.dataKey.replace('param_', '')
          const metadata = seriesMetadata[paramId] || {}
          return (
            <p key={index} style={{ color: entry.color }} className="text-xs">
              <span className="font-semibold">{metadata.name || `Parameter ${paramId}`}:</span> {entry.value?.toFixed(2)} {metadata.unit}
            </p>
          )
        })}
      </div>
    )
  }

  // Format X-axis based on time period
  const formatXAxisTick = (value) => {
    if (!value) return ''
    
    // For Year view, value is a month key like "2026-04", show as "Apr 26"
    if (timePeriod === 'Y' && typeof value === 'string' && value.includes('-')) {
      const [year, month] = value.split('-')
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const formatted = `${monthNames[parseInt(month) - 1]} ${year.slice(-2)}`
      console.log(`[formatXAxisTick] Year view: ${value} -> ${formatted}`)
      return formatted
    }
    
    // For other views, value is a timestamp
    const date = new Date(value)
    
    switch(timePeriod) {
      case 'M': // Month view: Show day
        return date.toLocaleString('en-US', { day: '2-digit' })
      case 'W': // Week view: Show weekday and day
        return date.toLocaleString('en-US', { weekday: 'short', day: '2-digit' })
      case 'D': // Day view: Show hour
        return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      default:
        return value
    }
  }

  // Calculate tick count and interval
  const getTickCount = () => {
    return Math.max(1, Math.ceil(graphData.length / 2))
  }

  return (
    <div className="w-full bg-white rounded border border-gray-200 p-4">
      {/* Debug Info (development mode) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600 border border-gray-300">
          <p>Selected Parameters: {selectedParameters.length} | Data Points: {graphData.length} | Loading: {loading ? 'Yes' : 'No'}</p>
          {selectedParameters.map(p => (
            <p key={p.id}>- ID: {p.id}, Name: {p.name}</p>
          ))}
          <p className="mt-1">Date Range: {dateRange.from} to {dateRange.to}</p>
        </div>
      )}
      
      {/* Chart - Always show structure with axes */}
      <div className="relative">
        {graphData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={graphData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="x"
                type={timePeriod === 'Y' ? 'category' : 'number'}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: timePeriod === 'Y' ? 11 : 12 }}
                angle={timePeriod === 'D' ? -45 : (timePeriod === 'Y' ? -45 : 0)}
                textAnchor={timePeriod === 'D' ? 'end' : (timePeriod === 'Y' ? 'end' : 'middle')}
                height={timePeriod === 'D' ? 100 : (timePeriod === 'Y' ? 120 : 60)}
                interval={0}
                minTickGap={0}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              {graphData.length > 0 && <Legend wrapperStyle={{ paddingTop: '20px' }} />}

              {/* Render a line for each selected parameter */}
              {selectedParameters.map((param, idx) => (
                <Line
                  key={param.id}
                  type="monotone"
                  dataKey={`param_${param.id}`}
                  stroke={COLORS[idx % COLORS.length]}
                  dot={false}
                  isAnimationActive={false}
                  name={seriesMetadata[param.id]?.name || param.name}
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          // Render empty chart structure when no data
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={[]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="x" type="number" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {/* Overlay message when no data */}
        {graphData.length === 0 && selectedParameters.length > 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded">
            <p className="text-gray-500 text-sm">No data available for the selected time period</p>
          </div>
        )}

        {selectedParameters.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded">
            <p className="text-gray-500 text-sm">Select parameters to display graph</p>
          </div>
        )}
      </div>
    </div>
  )
}
